import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  Autocomplete,
  Button,
  IconButton,
  Stack,
  Alert,
  alpha,
  Tooltip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  AccountCircle,
  Home,
  Description,
  Add,
  Delete,
  Groups,
  CloudUpload,
  CameraAlt,
  CheckCircle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import FileUploadComponent from '../common/FileUploadComponent';
import states from '../../utils/states.json';

const PersonalInformationSection = ({
  personalDraft,
  onPersonalDraftChange,
  onFileUpload,
  onFileRemove,
  validationErrors = {},
  isFresher,
  onClearError,
  requirements = [],
  employeeData = {},
  isSubmitting,
  rejectedDocs
}) => {
  const theme = useTheme();
  const [isSameAddress, setIsSameAddress] = useState(false);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  const getRejectionError = (docType) => {
    if (!rejectedDocs) return null;
    const doc = rejectedDocs.find(d => d.documentType === docType);
    return doc ? `Rejected: ${doc.rejectionReason}` : null;
  };

  const handleProfilePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingProfilePhoto(true);
    if (validationErrors.passportPhoto && onClearError) {
      onClearError('passportPhoto');
    }
    onFileUpload([file], 'personal', null, 'passportPhoto');
    setUploadingProfilePhoto(false);
    e.target.value = '';
  };

  const isProfilePhotoUploaded = () => {
    return (personalDraft.passportPhoto && personalDraft.passportPhoto.length > 0) || !!employeeData?.personalInfo?.passportPhotoPath;
  };

  const photo = personalDraft.passportPhoto && personalDraft.passportPhoto[0];

  useEffect(() => {
    if (!photo) {
      setPhotoPreview(null);
      return;
    }

    if (photo instanceof File) {
      const objectUrl = URL.createObjectURL(photo);
      setPhotoPreview(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else if (typeof photo === 'string') {
      setPhotoPreview(photo);
    } else if (photo && typeof photo === 'object' && photo.url) {
      setPhotoPreview(photo.url);
    } else {
      setPhotoPreview(null);
    }
  }, [photo]);

  const nominees = personalDraft?.nominees || [];
  const presentAddress = personalDraft?.presentAddress || {};
  const permanentAddress = personalDraft?.permanentAddress || {};
  const emergencyContact = personalDraft?.emergencyContact || {};

  // Initialize isSameAddress based on initial data
  useEffect(() => {
    const presentAddr = personalDraft?.presentAddress || {};
    const permanentAddr = personalDraft?.permanentAddress || {};
    const fields = ['houseNo', 'floor', 'houseName', 'street', 'landmark', 'city', 'state', 'pincode'];

    const hasPresent = !!presentAddr.houseNo;
    const allMatch = fields.every(f => (presentAddr[f] || '') === (permanentAddr[f] || ''));
    if (hasPresent && allMatch) {
      setIsSameAddress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNomineeChange = (index, field, value) => {
    let newNominees = [...nominees];
    if (field === 'sharePercentage') {
      newNominees[index] = { ...newNominees[index], sharePercentage: value };
    } else {
      newNominees[index] = { ...newNominees[index], [field]: value };
    }
    onPersonalDraftChange(prev => ({ ...prev, nominees: newNominees }));
    if (onClearError) {
      onClearError(`nomineeName_${index}`);
      onClearError(`nomineeRelation_${index}`);
      onClearError(`nomineeContact_${index}`);
      onClearError(`nomineeDob_${index}`);
      onClearError(`nomineeAadhaarNumber_${index}`);
      onClearError(`nomineeAadhaarFront_${index}`);
      onClearError(`nomineeAadhaarBack_${index}`);
    }
  };

  const addNominee = () => {
    let updatedNominees = [...nominees];
    updatedNominees.push({
      id: null,
      nomineeName: "",
      contactNo: "",
      nomineeDob: "",
      relation: "",
      aadhaarNumber: "",
      aadhaarFrontPath: "",
      aadhaarBackPath: "",
      sharePercentage: "",
      nomineeType: "Normal"
    });
    onPersonalDraftChange(prev => ({ ...prev, nominees: updatedNominees }));
  };

  const removeNominee = (index) => {
    const filteredNominees = nominees.filter((_, i) => i !== index);
    onPersonalDraftChange(prev => ({ ...prev, nominees: filteredNominees }));
  };

  const dependents = personalDraft?.dependents || [];

  const handleDependentChange = (index, field, value) => {
    let newDependents = [...dependents];
    newDependents[index] = { ...newDependents[index], [field]: value };
    onPersonalDraftChange(prev => ({ ...prev, dependents: newDependents }));
    if (onClearError) {
      onClearError(`dependentName_${index}`);
      onClearError(`dependentRelation_${index}`);
      onClearError(`dependentDob_${index}`);
    }
  };

  const addDependent = () => {
    let updatedDependents = [...dependents];
    updatedDependents.push({
      id: null,
      nomineeName: "",
      relation: "",
      nomineeDob: "",
      contactNo: "",
      aadhaarNumber: "",
      aadhaarFrontPath: "",
      aadhaarBackPath: "",
      nomineeType: "ESIC_Dependent"
    });
    onPersonalDraftChange(prev => ({ ...prev, dependents: updatedDependents }));
  };

  const removeDependent = (index) => {
    const filteredDependents = dependents.filter((_, i) => i !== index);
    onPersonalDraftChange(prev => ({ ...prev, dependents: filteredDependents }));
  };

  const clearError = useCallback((key) => {
    if (onClearError) onClearError(key);
  }, [onClearError]);

  const handleFieldChange = useCallback((field, value) => {
    onPersonalDraftChange(prev => ({ ...prev, [field]: value }));
    clearError(field);
  }, [onPersonalDraftChange, clearError]);

  const handleNestedFieldChange = useCallback((parent, field, value) => {
    onPersonalDraftChange(prev => {
      const updatedParent = {
        ...(prev[parent] || {}),
        [field]: value
      };

      const next = {
        ...prev,
        [parent]: updatedParent
      };

      if (parent === 'presentAddress' && isSameAddress && field !== 'accommodationType') {
        next.permanentAddress = {
          ...(next.permanentAddress || {}),
          [field]: value
        };
      }
      return next;
    });

    clearError(`${parent === 'presentAddress' ? 'present' : 'permanent'}_${field}`);
    if (parent === 'presentAddress' && isSameAddress && field !== 'accommodationType') {
      clearError(`permanent_${field}`);
    }
    if (field === 'accommodationType') clearError('accommodationType');
  }, [onPersonalDraftChange, clearError, isSameAddress]);

  const emergencyErrorKeyMap = useMemo(() => ({ relation: 'emergencyRelation', name: 'emergencyName', phone: 'emergencyPhone' }), []);

  const handleEmergencyContactChange = useCallback((field, value) => {
    onPersonalDraftChange(prev => ({
      ...prev,
      emergencyContact: {
        ...(prev.emergencyContact || {}),
        [field]: value
      }
    }));
    clearError(emergencyErrorKeyMap[field] || field);
  }, [onPersonalDraftChange, clearError, emergencyErrorKeyMap]);

  const collectESIC = employeeData?.compensation?.collectESIC;
  const hasESIC = collectESIC === true || collectESIC === 'true' || collectESIC === 'yes';

  // Premium Custom Card Styling
  const customCardStyle = {
    borderRadius: '16px',
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.08),
    boxShadow: '0 4px 20px -2px rgba(117, 34, 209, 0.02)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 30px -4px rgba(117, 34, 209, 0.08)',
      borderColor: alpha(theme.palette.primary.main, 0.2)
    },
    overflow: 'hidden'
  };

  const cardHeaderStyle = (title, icon) => (
    <Box sx={{
      px: { xs: 3, md: 4 },
      py: 3,
      borderBottom: '1px solid',
      borderColor: alpha(theme.palette.primary.main, 0.06),
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        color: 'primary.main'
      }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
        {title}
      </Typography>
    </Box>
  );

  const inputGlobalStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: '#F8FAFC',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#E2E8F0',
        transition: 'all 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        bgcolor: alpha(theme.palette.action.disabledBackground, 0.03),
      },
      '&.Mui-disabled fieldset': {
        borderColor: '#F1F5F9',
      },
      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error:hover fieldset': {
        borderColor: theme.palette.error.dark,
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiSelect-select': {
      borderRadius: '12px',
    }
  };

  return (
    <Container maxWidth="xl" sx={{ position: 'relative' }}>
      {isSubmitting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
        </Box>
      )}
      <Box sx={inputGlobalStyles}>
        {/* Top Welcome Banner Card */}
        <Card elevation={0} sx={{
          mb: 4,
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #4D1293 100%)`,
          color: '#FFFFFF',
          p: { xs: 3, md: 4 },
          position: 'relative',
          boxShadow: '0 10px 25px -5px rgba(117, 34, 209, 0.3)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.5px' }}>
                Personal Information
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#FFFFFF', 0.8), fontWeight: 400 }}>
                Let us get to know you. Please review and fill out your verification details below.
              </Typography>
            </Box>
            <AccountCircle sx={{ fontSize: 80, color: alpha('#FFFFFF', 0.15), display: { xs: 'none', md: 'block' } }} />
          </Box>
        </Card>

        <Stack spacing={4} sx={{ mb: 4 }}>
          {/* CARD 0: PROFILE PHOTO */}
          <Card elevation={0} sx={customCardStyle}>
            {cardHeaderStyle(
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                Profile Photo<Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>(Optional)</Typography>
              </Box>,
              <CameraAlt />
            )}
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ position: 'relative' }}>
                    {/* Glowing outer ring */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -6,
                        left: -6,
                        right: -6,
                        bottom: -6,
                        borderRadius: '50%',
                        background: isProfilePhotoUploaded()
                          ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                          : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #A78BFA 100%)`,
                        opacity: 0.8,
                        filter: 'blur(3px)',
                        transition: 'all 0.3s ease',
                      }}
                    />
                    <Avatar
                      src={photoPreview || employeeData?.personalInfo?.passportPhotoPath || undefined}
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: '#F8FAFC',
                        color: 'primary.main',
                        fontSize: '2.5rem',
                        fontWeight: 800,
                        border: '4px solid #FFFFFF',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        position: 'relative',
                        zIndex: 2,
                      }}
                    >
                      {!(photoPreview || employeeData?.personalInfo?.passportPhotoPath) && (
                        <CameraAlt sx={{ fontSize: '2.5rem', color: 'rgba(117, 34, 209, 0.4)' }} />
                      )}
                    </Avatar>

                    {/* Upload hover button */}
                    <Box
                      component="label"
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        border: '3px solid #FFFFFF',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 3,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#5a1aa8',
                          transform: 'scale(1.1)'
                        },
                        boxShadow: '0 4px 10px rgba(117,34,209,0.3)',
                      }}
                    >
                      {uploadingProfilePhoto ? (
                        <CircularProgress size={16} sx={{ color: '#fff' }} />
                      ) : (
                        <CameraAlt sx={{ fontSize: 18 }} />
                      )}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleProfilePhotoUpload}
                      />
                    </Box>

                    {isProfilePhotoUploaded() && (
                      <Box sx={{
                        position: 'absolute',
                        bottom: 4,
                        left: -2,
                        width: 24,
                        height: 24,
                        bgcolor: '#10B981',
                        borderRadius: '50%',
                        border: '2px solid #FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 3,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                      }}>
                        <CheckCircle sx={{ fontSize: 16, color: '#fff' }} />
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                  <Typography variant="body2" fontWeight={700} color="#1E293B" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    Profile Photograph
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, lineHeight: 1.4 }}>
                    Upload a profile picture for your employee record. This photo will be visible on your portal dashboard.
                  </Typography>

                  {/* Photo Guidelines checklist */}
                  <Stack spacing={0.5} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>Clear face, neutral background</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>JPG, JPEG, or PNG format</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>Maximum file size: 5MB</Typography>
                    </Stack>
                  </Stack>

                  {validationErrors.passportPhoto && (
                    <Typography variant="caption" color="error" sx={{ fontWeight: 600, display: 'block' }}>
                      {validationErrors.passportPhoto}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* CARD 1: BASIC INFORMATION */}
          <Card elevation={0} sx={customCardStyle}>
            {cardHeaderStyle("Basic Information", <AccountCircle />)}
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="First Name (As per Aadhar)"
                    variant="outlined"
                    value={personalDraft.firstName || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleFieldChange('firstName', value);
                    }}
                    fullWidth
                    required
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.firstName}
                    helperText={validationErrors.firstName || "Letters only, max 50 characters"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Middle Name (As per Aadhar)"
                    variant="outlined"
                    value={personalDraft.middleName || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleFieldChange('middleName', value);
                    }}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.middleName}
                    helperText={validationErrors.middleName || "Letters only, max 50 characters"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Last Name (As per Aadhar)"
                    variant="outlined"
                    value={personalDraft.lastName || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleFieldChange('lastName', value);
                    }}
                    fullWidth
                    required
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.lastName}
                    helperText={validationErrors.lastName || "Letters only, max 50 characters"}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Father Name"
                    variant="outlined"
                    value={personalDraft.fatherName || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleFieldChange('fatherName', value);
                    }}
                    fullWidth
                    required
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.fatherName}
                    helperText={validationErrors.fatherName || "Letters only, max 50 characters"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Mother Name"
                    variant="outlined"
                    value={personalDraft.motherName || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleFieldChange('motherName', value);
                    }}
                    fullWidth
                    required
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.motherName}
                    helperText={validationErrors.motherName || "Letters only, max 50 characters"}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Personal Email Address"
                    variant="outlined"
                    value={personalDraft.personalEmail || ""}
                    onChange={e => handleFieldChange('personalEmail', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.personalEmail}
                    helperText={validationErrors.personalEmail || "Your personal email address"}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Alternative Phone Number"
                    variant="outlined"
                    value={personalDraft.alternativePhone || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleFieldChange('alternativePhone', value);
                    }}
                    fullWidth
                    required
                    inputProps={{ maxLength: 10 }}
                    error={!!validationErrors.alternativePhone}
                    helperText={validationErrors.alternativePhone || "Enter 10-digit phone number"}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.gender}>
                    <InputLabel>Gender*</InputLabel>
                    <Select
                      value={personalDraft.gender || ''}
                      onChange={e => handleFieldChange('gender', e.target.value)}
                      label="Gender"
                      required
                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Non-binary">Non-binary</MenuItem>
                      <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                    </Select>
                    {validationErrors.gender && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.gender}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    value={personalDraft.dob || ''}
                    onChange={e => handleFieldChange('dob', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.dob}
                    helperText={validationErrors.dob || "Must be 18 years or older"}
                    inputProps={{
                      max: new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.nationality}>
                    <InputLabel>Nationality*</InputLabel>
                    <Select
                      value={personalDraft.nationality || 'Indian'}
                      onChange={e => handleFieldChange('nationality', e.target.value)}
                      label="Nationality"
                      required
                    >
                      <MenuItem value="Indian">Indian</MenuItem>
                    </Select>
                    {validationErrors.nationality && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.nationality}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.religion}>
                    <InputLabel>Religion*</InputLabel>
                    <Select
                      value={personalDraft.religion || ''}
                      onChange={e => handleFieldChange('religion', e.target.value)}
                      label="Religion"
                      required
                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="Hindu">Hindu</MenuItem>
                      <MenuItem value="Muslim">Muslim</MenuItem>
                      <MenuItem value="Christian">Christian</MenuItem>
                      <MenuItem value="Sikh">Sikh</MenuItem>
                      <MenuItem value="Buddhist">Buddhist</MenuItem>
                      <MenuItem value="Jain">Jain</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {validationErrors.religion && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.religion}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.bloodGroup}>
                    <InputLabel>Blood Group*</InputLabel>
                    <Select
                      value={personalDraft.bloodGroup || ''}
                      onChange={e => handleFieldChange('bloodGroup', e.target.value)}
                      label="Blood Group"
                      required
                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="A+">A+</MenuItem>
                      <MenuItem value="A-">A-</MenuItem>
                      <MenuItem value="B+">B+</MenuItem>
                      <MenuItem value="B-">B-</MenuItem>
                      <MenuItem value="O+">O+</MenuItem>
                      <MenuItem value="O-">O-</MenuItem>
                      <MenuItem value="AB+">AB+</MenuItem>
                      <MenuItem value="AB-">AB-</MenuItem>
                    </Select>
                    {validationErrors.bloodGroup && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.bloodGroup}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.maritalStatus}>
                    <InputLabel>Marital Status*</InputLabel>
                    <Select
                      value={personalDraft.maritalStatus || ''}
                      onChange={e => handleFieldChange('maritalStatus', e.target.value)}
                      label="Marital Status"
                      required
                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="Single">Single</MenuItem>
                      <MenuItem value="Married">Married</MenuItem>
                      <MenuItem value="Widowed">Widowed</MenuItem>
                      <MenuItem value="Separated">Separated</MenuItem>
                    </Select>
                    {validationErrors.maritalStatus && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.maritalStatus}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {personalDraft.maritalStatus === 'Married' && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      label="Spouse Name"
                      variant="outlined"
                      value={personalDraft.spouseName || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                        handleFieldChange('spouseName', value);
                      }}
                      fullWidth
                      required
                      inputProps={{ maxLength: 50 }}
                      error={!!validationErrors.spouseName}
                      helperText={validationErrors.spouseName}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.physicallyChallenged}>
                    <InputLabel>Physically Handicapped*</InputLabel>
                    <Select
                      value={personalDraft.physicallyChallenged || 'No'}
                      onChange={e => handleFieldChange('physicallyChallenged', e.target.value)}
                      label="Physically Handicapped"
                      required
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                    {validationErrors.physicallyChallenged && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.physicallyChallenged}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {personalDraft.physicallyChallenged === 'Yes' && (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Disability Details"
                        variant="outlined"
                        value={personalDraft.disabilityDetails || ''}
                        onChange={e => handleFieldChange('disabilityDetails', e.target.value)}
                        fullWidth
                        required
                        error={!!validationErrors.disabilityDetails}
                        helperText={validationErrors.disabilityDetails}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FileUploadComponent
                        label="Upload Disability Certificate"
                        files={personalDraft.disabilityCertificate || []}
                        onFileUpload={(files) => onFileUpload(files, 'personal', null, 'disabilityCertificate')}
                        onFileRemove={(index) => onFileRemove('personal', null, index, 'disabilityCertificate')}
                        multiple={false}
                        required
                        error={!!validationErrors.disabilityCertificate}
                        helperText={validationErrors.disabilityCertificate}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* CARD 2: EMERGENCY CONTACT DETAILS */}
          <Card elevation={0} sx={customCardStyle}>
            {cardHeaderStyle("Emergency Contact Details", <Groups />)}
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.emergencyRelation}>
                    <InputLabel>Relation*</InputLabel>
                    <Select
                      value={emergencyContact.relation || ''}
                      onChange={e => handleEmergencyContactChange('relation', e.target.value)}
                      label="Relation"
                      required
                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="Father">Father</MenuItem>
                      <MenuItem value="Mother">Mother</MenuItem>
                      <MenuItem value="Spouse">Spouse</MenuItem>
                      <MenuItem value="Sibling">Sibling</MenuItem>
                      <MenuItem value="Child">Child</MenuItem>
                      <MenuItem value="Father-in-law">Father-in-law</MenuItem>
                      <MenuItem value="Mother-in-law">Mother-in-law</MenuItem>
                    </Select>
                    {validationErrors.emergencyRelation && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.emergencyRelation}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Name"
                    variant="outlined"
                    value={emergencyContact.name || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleEmergencyContactChange('name', value);
                    }}
                    fullWidth
                    required
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.emergencyName}
                    helperText={validationErrors.emergencyName || "Letters only, max 50 characters"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Phone No"
                    variant="outlined"
                    value={emergencyContact.phone || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleEmergencyContactChange('phone', value);
                    }}
                    fullWidth
                    required
                    inputProps={{ maxLength: 10 }}
                    error={!!validationErrors.emergencyPhone}
                    helperText={validationErrors.emergencyPhone || "Enter 10-digit phone number"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* CARD 3: PRESENT ADDRESS */}
          <Card elevation={0} sx={customCardStyle}>
            {cardHeaderStyle("Present Address", <Home />)}
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth error={!!validationErrors.accommodationType}>
                    <InputLabel>Accommodation Type*</InputLabel>
                    <Select
                      value={presentAddress.accommodationType || ''}
                      onChange={e => handleNestedFieldChange('presentAddress', 'accommodationType', e.target.value)}
                      label="Accommodation Type"
                      required
                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="Rental">Rental</MenuItem>
                      <MenuItem value="Own">Own</MenuItem>
                      <MenuItem value="Living with Parents">Living with Parents</MenuItem>
                    </Select>
                    {validationErrors.accommodationType && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {validationErrors.accommodationType}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Flat / House no"
                    variant="outlined"
                    value={presentAddress.houseNo || ''}
                    onChange={e => handleNestedFieldChange('presentAddress', 'houseNo', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.present_houseNo}
                    helperText={validationErrors.present_houseNo}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Floor"
                    variant="outlined"
                    value={presentAddress.floor || ''}
                    onChange={e => handleNestedFieldChange('presentAddress', 'floor', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.present_floor}
                    helperText={validationErrors.present_floor}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Building / House name"
                    variant="outlined"
                    value={presentAddress.houseName || ''}
                    onChange={e => handleNestedFieldChange('presentAddress', 'houseName', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.present_houseName}
                    helperText={validationErrors.present_houseName}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Area/Street/Sector/Locality"
                    variant="outlined"
                    value={presentAddress.street || ''}
                    onChange={e => handleNestedFieldChange('presentAddress', 'street', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.present_street}
                    helperText={validationErrors.present_street}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Nearby Landmark"
                    variant="outlined"
                    value={presentAddress.landmark || ''}
                    onChange={e => handleNestedFieldChange('presentAddress', 'landmark', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.present_landmark}
                    helperText={validationErrors.present_landmark}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="City"
                    variant="outlined"
                    value={presentAddress.city || ''}
                    onChange={e => handleNestedFieldChange('presentAddress', 'city', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors.present_city}
                    helperText={validationErrors.present_city}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Autocomplete
                    fullWidth
                    options={states}
                    value={presentAddress.state || null}
                    onChange={(event, newValue) => handleNestedFieldChange('presentAddress', 'state', newValue || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        variant="outlined"
                        required
                        error={!!validationErrors.present_state}
                        helperText={validationErrors.present_state}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Pincode"
                    variant="outlined"
                    value={presentAddress.pincode || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleNestedFieldChange('presentAddress', 'pincode', value);
                    }}
                    fullWidth
                    required
                    error={!!validationErrors.present_pincode}
                    helperText={validationErrors.present_pincode || "Enter numbers only"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{
            ...customCardStyle,
            opacity: isSameAddress ? 0.8 : 1,
            bgcolor: isSameAddress ? alpha(theme.palette.action.disabledBackground, 0.05) : 'background.paper',
            borderColor: isSameAddress ? theme.palette.divider : alpha(theme.palette.primary.main, 0.08)
          }}>
            {cardHeaderStyle("Permanent Address", <Home />)}
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Flat / House no"
                    variant="outlined"
                    value={permanentAddress.houseNo || ''}
                    onChange={e => handleNestedFieldChange('permanentAddress', 'houseNo', e.target.value)}
                    fullWidth
                    required
                    disabled={isSameAddress}
                    error={!isSameAddress && !!validationErrors.permanent_houseNo}
                    helperText={!isSameAddress && validationErrors.permanent_houseNo}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Floor"
                    variant="outlined"
                    value={permanentAddress.floor || ''}
                    onChange={e => handleNestedFieldChange('permanentAddress', 'floor', e.target.value)}
                    fullWidth
                    required
                    disabled={isSameAddress}
                    error={!isSameAddress && !!validationErrors.permanent_floor}
                    helperText={!isSameAddress && validationErrors.permanent_floor}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Building / House name"
                    variant="outlined"
                    value={permanentAddress.houseName || ''}
                    onChange={e => handleNestedFieldChange('permanentAddress', 'houseName', e.target.value)}
                    fullWidth
                    required
                    disabled={isSameAddress}
                    error={!isSameAddress && !!validationErrors.permanent_houseName}
                    helperText={!isSameAddress && validationErrors.permanent_houseName}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Area/Street/Sector/Locality"
                    variant="outlined"
                    value={permanentAddress.street || ''}
                    onChange={e => handleNestedFieldChange('permanentAddress', 'street', e.target.value)}
                    fullWidth
                    required
                    disabled={isSameAddress}
                    error={!isSameAddress && !!validationErrors.permanent_street}
                    helperText={!isSameAddress && validationErrors.permanent_street}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Nearby Landmark"
                    variant="outlined"
                    value={permanentAddress.landmark || ''}
                    onChange={e => handleNestedFieldChange('permanentAddress', 'landmark', e.target.value)}
                    fullWidth
                    required
                    disabled={isSameAddress}
                    error={!isSameAddress && !!validationErrors.permanent_landmark}
                    helperText={!isSameAddress && validationErrors.permanent_landmark}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="City"
                    variant="outlined"
                    value={permanentAddress.city || ''}
                    onChange={e => handleNestedFieldChange('permanentAddress', 'city', e.target.value)}
                    fullWidth
                    required
                    disabled={isSameAddress}
                    error={!isSameAddress && !!validationErrors.permanent_city}
                    helperText={!isSameAddress && validationErrors.permanent_city}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Autocomplete
                    fullWidth
                    options={states}
                    value={permanentAddress.state || null}
                    onChange={(event, newValue) => handleNestedFieldChange('permanentAddress', 'state', newValue || "")}
                    disabled={isSameAddress}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        variant="outlined"
                        required
                        error={!isSameAddress && !!validationErrors.permanent_state}
                        helperText={!isSameAddress && validationErrors.permanent_state}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Pincode"
                    variant="outlined"
                    value={permanentAddress.pincode || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '');
                      handleNestedFieldChange('permanentAddress', 'pincode', value);
                    }}
                    fullWidth
                    required
                    disabled={isSameAddress}
                    error={!isSameAddress && !!validationErrors.permanent_pincode}
                    helperText={!isSameAddress && (validationErrors.permanent_pincode || "Enter numbers only")}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* CARD 5: IDENTIFICATION DOCUMENTS */}
          <Card elevation={0} sx={customCardStyle}>
            {cardHeaderStyle("Identification Documents", <Description />)}
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Aadhar Number"
                    variant="outlined"
                    value={personalDraft.aadharNo || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                      handleFieldChange('aadharNo', value);
                    }}
                    fullWidth
                    inputProps={{ maxLength: 12 }}
                    error={!!validationErrors.aadharNo}
                    helperText={validationErrors.aadharNo || "Enter 12-digit Aadhaar number"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Name as per Aadhar"
                    variant="outlined"
                    value={personalDraft.aadharName || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleFieldChange('aadharName', value);
                    }}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.aadharName}
                    helperText={validationErrors.aadharName || "Letters only, max 50 characters"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FileUploadComponent
                    label="Upload Aadhaar (Front Side)"
                    files={personalDraft.aadharFrontDocument || []}
                    onFileUpload={(files) => onFileUpload(files, 'personal', null, 'aadharFront')}
                    onFileRemove={(index) => onFileRemove('personal', null, index, 'aadharFront')}
                    multiple={false}
                    required
                    error={!!validationErrors.aadharFrontDocument || !!getRejectionError('Aadhar Front')}
                    helperText={getRejectionError('Aadhar Front') || validationErrors.aadharFrontDocument}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FileUploadComponent
                    label="Upload Aadhaar (Back Side)"
                    files={personalDraft.aadharBackDocument || []}
                    onFileUpload={(files) => onFileUpload(files, 'personal', null, 'aadharBack')}
                    onFileRemove={(index) => onFileRemove('personal', null, index, 'aadharBack')}
                    multiple={false}
                    required
                    error={!!validationErrors.aadharBackDocument || !!getRejectionError('Aadhar Back')}
                    helperText={getRejectionError('Aadhar Back') || validationErrors.aadharBackDocument}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="PAN"
                    variant="outlined"
                    value={personalDraft.panNo || ''}
                    onChange={e => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
                      handleFieldChange('panNo', value);
                    }}
                    fullWidth
                    inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                    error={!!validationErrors.panNo}
                    helperText={validationErrors.panNo || "Enter 10-character PAN"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Name as per PAN"
                    variant="outlined"
                    value={personalDraft.panName || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
                      handleFieldChange('panName', value);
                    }}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    error={!!validationErrors.panName}
                    helperText={validationErrors.panName || "Letters only, max 50 characters"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FileUploadComponent
                    label="Upload PAN"
                    files={personalDraft.panDocument || []}
                    onFileUpload={(files) => onFileUpload(files, 'personal', null, 'pan')}
                    onFileRemove={(index) => onFileRemove('personal', null, index, 'pan')}
                    multiple={false}
                    required
                    error={!!validationErrors.panDocument || !!getRejectionError('PAN')}
                    helperText={getRejectionError('PAN') || validationErrors.panDocument}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="UAN No."
                    variant="outlined"
                    value={personalDraft.uanNo || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                      handleFieldChange('uanNo', value);
                    }}
                    fullWidth
                    required={!isFresher}
                    inputProps={{ maxLength: 12 }}
                    error={!!validationErrors.uanNo}
                    helperText={validationErrors.uanNo || "Enter 12-digit UAN number"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="ESIC No."
                    variant="outlined"
                    value={personalDraft.esicNo || ''}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 17);
                      handleFieldChange('esicNo', value);
                    }}
                    fullWidth
                    required={!isFresher}
                    inputProps={{ maxLength: 17 }}
                    error={!!validationErrors.esicNo}
                    helperText={validationErrors.esicNo || "Enter 17-digit ESIC number"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel>Add Details (Optional)</InputLabel>
                    <Select
                      value={personalDraft.additionalIdType || ''}
                      onChange={e => handleFieldChange('additionalIdType', e.target.value)}
                      label="Add Details (Optional)"
                    >
                      <MenuItem value="">Select ID Type</MenuItem>
                      <MenuItem value="Voter">Voter ID</MenuItem>
                      <MenuItem value="Driving Licence">Driving Licence</MenuItem>
                      <MenuItem value="Passport">Passport</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {personalDraft.additionalIdType && (
                  <>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label={`${personalDraft.additionalIdType} Number`}
                        variant="outlined"
                        value={personalDraft.additionalIdNumber || ''}
                        onChange={e => handleFieldChange('additionalIdNumber', e.target.value)}
                        fullWidth
                        error={!!validationErrors.additionalIdNumber || !!getRejectionError(personalDraft.additionalIdType)}
                        helperText={getRejectionError(personalDraft.additionalIdType) || validationErrors.additionalIdNumber}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FileUploadComponent
                        label={`Upload ${personalDraft.additionalIdType}`}
                        files={personalDraft.additionalIdDocument || []}
                        onFileUpload={(files) => onFileUpload(files, 'personal', null, 'additionalId')}
                        onFileRemove={(index) => onFileRemove('personal', null, index, 'additionalId')}
                        multiple={false}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* CARD 6: NOMINEE DETAILS */}
          <Card elevation={0} sx={customCardStyle}>
            <Box sx={{
              px: { xs: 3, md: 4 },
              py: 3,
              borderBottom: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.06),
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main'
                }}>
                  <Groups />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Nominee Details
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={addNominee}
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderColor: 'primary.main'
                  }
                }}
              >
                Add Nominee
              </Button>
            </Box>

            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {nominees.map((nominee, idx) => (
                <Box key={nominee.id || idx} sx={{
                  mb: idx === nominees.length - 1 ? 0 : 4,
                  p: 3,
                  borderRadius: '12px',
                  bgcolor: alpha(theme.palette.primary.main, 0.015),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, 0.06),
                  position: 'relative',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    borderColor: alpha(theme.palette.primary.main, 0.15)
                  }
                }}>
                  {/* Delete Button */}
                  {nominees.length > 1 && (
                    <Tooltip title="Remove Nominee" placement="top">
                      <IconButton
                        size="small"
                        color="error"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          bgcolor: alpha(theme.palette.error.main, 0.05),
                          '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12) }
                        }}
                        onClick={() => removeNominee(idx)}
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Header Indicator */}
                  <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
                    Nominee #{idx + 1}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Nominee Name*"
                        size="small"
                        fullWidth
                        value={nominee.nomineeName || ''}
                        onChange={(e) => handleNomineeChange(idx, 'nomineeName', e.target.value)}
                        error={!!validationErrors[`nomineeName_${idx}`]}
                        helperText={validationErrors[`nomineeName_${idx}`]}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Contact No*"
                        size="small"
                        fullWidth
                        value={nominee.contactNo || ''}
                        onChange={(e) => handleNomineeChange(idx, 'contactNo', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        error={!!validationErrors[`nomineeContact_${idx}`]}
                        helperText={validationErrors[`nomineeContact_${idx}`]}
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="DOB*"
                        size="small"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={nominee.nomineeDob || ''}
                        onChange={(e) => handleNomineeChange(idx, 'nomineeDob', e.target.value)}
                        error={!!validationErrors[`nomineeDob_${idx}`]}
                        helperText={validationErrors[`nomineeDob_${idx}`]}
                        inputProps={{
                          max: new Date().toISOString().split('T')[0]
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormControl fullWidth size="small" error={!!validationErrors[`nomineeRelation_${idx}`]}>
                        <InputLabel id={`nominee-relation-label-${idx}`}>Relation*</InputLabel>
                        <Select
                          labelId={`nominee-relation-label-${idx}`}
                          value={nominee.relation || ''}
                          label="Relation*"
                          onChange={(e) => handleNomineeChange(idx, 'relation', e.target.value)}
                        >
                          <MenuItem value="Father" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Father')}>Father</MenuItem>
                          <MenuItem value="Mother" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Mother')}>Mother</MenuItem>
                          <MenuItem value="Spouse" disabled={nominees.some((n, i) => i !== idx && n.relation === 'Spouse')}>Spouse</MenuItem>
                          <MenuItem value="Sibling">Sibling</MenuItem>
                          <MenuItem value="Child">Child</MenuItem>
                        </Select>
                        {validationErrors[`nomineeRelation_${idx}`] && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                            {validationErrors[`nomineeRelation_${idx}`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Share Percentage (%)"
                        size="small"
                        type="number"
                        fullWidth
                        value={nominee.sharePercentage !== undefined ? nominee.sharePercentage : 100.00}
                        onChange={(e) => handleNomineeChange(idx, 'sharePercentage', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        label="Nominee Aadhaar Number*"
                        size="small"
                        fullWidth
                        value={nominee.aadhaarNumber || ''}
                        onChange={(e) => handleNomineeChange(idx, 'aadhaarNumber', e.target.value.replace(/\D/g, '').slice(0, 12))}
                        error={!!validationErrors[`nomineeAadhaarNumber_${idx}`]}
                        helperText={validationErrors[`nomineeAadhaarNumber_${idx}`] || "Enter 12-digit Aadhaar number"}
                        inputProps={{ maxLength: 12 }}
                      />
                    </Grid>

                    {/* Aadhaar File Uploads (Nominee) */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Aadhaar Front Copy*
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          startIcon={<CloudUpload />}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05), borderColor: 'primary.main' }
                          }}
                        >
                          Upload Front (Max 4MB)
                          <input
                            type="file"
                            hidden
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleNomineeChange(idx, 'aadhaarFrontFile', e.target.files[0]);
                              }
                            }}
                          />
                        </Button>
                        {nominee.aadhaarFrontFile && (
                          <Typography variant="caption" noWrap sx={{ maxWidth: 150 }} color="success.main">
                            {nominee.aadhaarFrontFile.name}
                          </Typography>
                        )}
                        {!nominee.aadhaarFrontFile && nominee.aadhaarFrontPath && (
                          <Typography variant="caption" noWrap sx={{ maxWidth: 150 }} color="primary.main">
                            Uploaded
                          </Typography>
                        )}
                      </Stack>
                      {validationErrors[`nomineeAadhaarFront_${idx}`] && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                          {validationErrors[`nomineeAadhaarFront_${idx}`]}
                        </Typography>
                      )}
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Aadhaar Back Copy*
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          variant="outlined"
                          component="label"
                          size="small"
                          startIcon={<CloudUpload />}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05), borderColor: 'primary.main' }
                          }}
                        >
                          Upload Back (Max 4MB)
                          <input
                            type="file"
                            hidden
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleNomineeChange(idx, 'aadhaarBackFile', e.target.files[0]);
                              }
                            }}
                          />
                        </Button>
                        {nominee.aadhaarBackFile && (
                          <Typography variant="caption" noWrap sx={{ maxWidth: 150 }} color="success.main">
                            {nominee.aadhaarBackFile.name}
                          </Typography>
                        )}
                        {!nominee.aadhaarBackFile && nominee.aadhaarBackPath && (
                          <Typography variant="caption" noWrap sx={{ maxWidth: 150 }} color="primary.main">
                            Uploaded
                          </Typography>
                        )}
                      </Stack>
                      {validationErrors[`nomineeAadhaarBack_${idx}`] && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                          {validationErrors[`nomineeAadhaarBack_${idx}`]}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ))}

              {validationErrors.nominees && (
                <Alert severity="error" sx={{ mt: 3, borderRadius: '8px' }}>{validationErrors.nominees}</Alert>
              )}
            </CardContent>
          </Card>



        </Stack>
      </Box>
    </Container>
  );
};

export default PersonalInformationSection;
